
import { Link, useLocation } from "react-router-dom";
import { Database, Sparkles, CheckCircle2, BookOpen, ArrowLeft, Layers } from "lucide-react";
import type { KnowledgeChunkDetailResponse, KnowledgeChunkResponse } from "../../gen/models";

interface ChunkSidebarPanelsProps {
  chunk: KnowledgeChunkDetailResponse;
  siblingChunks: KnowledgeChunkResponse[];
}

export function ChunkSidebarPanels({ chunk, siblingChunks }: ChunkSidebarPanelsProps) {
  const location = useLocation();
  return (
    <div className="space-y-6">
      {/* Vector Embeddings Card */}
      <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-5 shadow-2xs space-y-4">
        <div className="border-b border-stone-100 dark:border-stone-800 pb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-stone-800 dark:text-stone-200">
              Vector Representation
            </h3>
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-semibold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/80">
            <CheckCircle2 className="w-2.5 h-2.5" />
            <span>{chunk.embedding_dim || 384}-dim</span>
          </span>
        </div>

        <div className="rounded-lg border border-emerald-200 dark:border-emerald-800/80 bg-emerald-50/50 dark:bg-emerald-950/40 p-3.5 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <div>
              <span className="font-bold text-emerald-950 dark:text-emerald-300 font-mono">
                {chunk.embedding_model || "bge-small-en-v1.5"}
              </span>
              <span className="text-[11px] text-emerald-800 dark:text-emerald-400 block pt-0.5">
                FastEmbed cosine index active
              </span>
            </div>
          </div>
          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white shrink-0">
            Online
          </span>
        </div>

        <div className="space-y-2 text-xs pt-1">
          <div className="flex justify-between py-1 border-b border-stone-100 dark:border-stone-800 text-stone-500 dark:text-stone-400">
            <span>Vector Dimension</span>
            <span className="font-mono font-semibold text-stone-800 dark:text-stone-200">
              {chunk.embedding_dim || 384}
            </span>
          </div>
          <div className="flex justify-between py-1 border-b border-stone-100 dark:border-stone-800 text-stone-500 dark:text-stone-400">
            <span>Distance Metric</span>
            <span className="font-mono font-semibold text-stone-800 dark:text-stone-200">
              Cosine Distance
            </span>
          </div>
          <div className="flex justify-between py-1 text-stone-500 dark:text-stone-400">
            <span>Segment Index</span>
            <span className="font-mono font-semibold text-cyan-600 dark:text-cyan-400">
              #{chunk.chunk_index} of {siblingChunks.length || 1}
            </span>
          </div>
        </div>
      </div>

      {/* Parent Document Card */}
      <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-5 shadow-2xs space-y-3">
        <div className="border-b border-stone-100 dark:border-stone-800 pb-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-stone-800 dark:text-stone-200">
              Parent Document
            </h3>
          </div>
          {chunk.item_status && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border border-stone-200 dark:border-stone-700">
              {chunk.item_status}
            </span>
          )}
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-stone-900 dark:text-stone-100">
            {chunk.item_title || "Document"}
          </h4>
          <div className="text-xs text-stone-500 dark:text-stone-400 space-y-1">
            <div className="flex justify-between">
              <span>Category:</span>
              <span className="font-medium text-stone-700 dark:text-stone-300 capitalize">
                {chunk.item_category || "General"}
              </span>
            </div>
            {chunk.item_source_type && (
              <div className="flex justify-between">
                <span>Source Type:</span>
                <span className="font-medium text-stone-700 dark:text-stone-300 capitalize">
                  {chunk.item_source_type}
                </span>
              </div>
            )}
            {chunk.item_id && (
              <div className="flex justify-between">
                <span>Item ID:</span>
                <code className="font-mono text-[11px] text-stone-600 dark:text-stone-400">
                  {chunk.item_id.slice(0, 8)}…
                </code>
              </div>
            )}
          </div>

          {chunk.item_id && (
            <div className="pt-2">
              <Link
                to={{ pathname: `/knowledge/${chunk.item_id}`, search: location.search }}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800 text-xs font-medium text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-700 transition"
              >
                <span>Open Knowledge Item</span>
                <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Sibling Chunks Selector */}
      {siblingChunks.length > 0 && (
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-5 shadow-2xs space-y-3">
          <div className="border-b border-stone-100 dark:border-stone-800 pb-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-stone-800 dark:text-stone-200">
                Document Chunks ({siblingChunks.length})
              </h3>
            </div>
          </div>

          <div className="space-y-1.5 max-h-[300px] overflow-y-scroll [scrollbar-gutter:stable] pr-1">
            {siblingChunks.map((sib, sIdx) => {
              const isCurrent = sib.id === chunk.id;
              return (
                <Link
                  key={sib.id || sIdx}
                  to={{ pathname: `/knowledge/chunk/${sib.id}`, search: location.search }}
                  className={`block p-2 rounded-lg border text-xs transition ${
                    isCurrent
                      ? "bg-cyan-950/60 border-cyan-700 text-cyan-200 shadow-2xs"
                      : "bg-stone-50/60 dark:bg-stone-800/40 border-stone-200 dark:border-stone-800 text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800"
                  }`}
                >
                  <div className="flex items-center justify-between font-mono text-[11px]">
                    <span className="font-bold">
                      Chunk #{sib.chunk_index !== undefined ? sib.chunk_index : sIdx}
                    </span>
                    {isCurrent && (
                      <span className="text-[9px] uppercase font-bold px-1.5 py-0.2 rounded bg-cyan-800/80 text-cyan-100">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="line-clamp-2 text-[11px] text-stone-500 dark:text-stone-400 mt-1 font-sans">
                    {sib.chunk_text}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
